import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import ParcelCreationProgress from '../../features/parcel-creation/parcel-creation-progress/parcel-creation-progress.component';
import ParcelCreationProgressMobile from '../../features/parcel-creation/parcel-creation-progress/parcel-creation-progress-mobile.component';
import {
  ParcelStepLeftContainer,
  ParcelStepMobileContainer,
  ParcelStepMobileCotentContainer,
} from '../../features/parcel-creation/parcel-step-left-container/parcel-step-left-container.styles';
import { ParcelStepRightContainer } from '../../features/parcel-creation/parcel-step-right-container/parcel-step-right-container.styles';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import { useStore } from '../../stores/store';
import {
  DesktopParcelWrapper,
  ParcelTitle,
  WrapperForParcelContainers,
} from '../../features/parcel-creation/parcel-title-and-wrapper/parcel-title-and-wrapper.styles';
import { useLoadPlacesLibs } from '../../hooks/use-load-places-libs.hook';
import { AppRoute } from '../../constants/route';
import ParcelCreationBottom from '../../features/parcel-creation/parcel-creation-bottom/parcel-creation-bottom.component';
import { useNavigationBlocker } from '../../hooks/use-navigation-blocker.hook';
import { toTranslationKey } from '../../models/parcelCreationFlowNavigation';
import { generateStartParcelCreationRoute } from '../../utils/uri/uri.utils';
import {
  ParcelHistoryNavigationContext,
  useParcelHistoryNavigation,
} from '../../hooks/use-parcel-history-navigation.hook';

const parcelCreationContentDesktop = (isDisabled?: boolean) => (
  <>
    <ParcelStepLeftContainer $disabled={isDisabled}>
      <Outlet />
    </ParcelStepLeftContainer>
    <ParcelStepRightContainer>
      <ParcelCreationProgress />
    </ParcelStepRightContainer>
  </>
);

const parcelCreationContentMobile = (isDisabled?: boolean) => (
  <ParcelStepMobileContainer>
    <ParcelCreationProgressMobile />
    <ParcelStepMobileCotentContainer $disabled={isDisabled}>
      <Outlet />
    </ParcelStepMobileCotentContainer>
    <ParcelCreationBottom />
  </ParcelStepMobileContainer>
);

const ParcelCreation = () => {
  const isMobile = useIsMobile();
  const location = useLocation();

  const {
    parcelCreationStore: {
      navigation,
      isParcelCreationInProgress,
      isNextStepButtonLoading,
      setIsParcelCreationInProgress,
      resetAllData,
      canSkipVerifyPhone,
    },
    userStore: { loginGup },
    commonStore: { isGuestParcelCreation },
  } = useStore();
  const { t } = useTranslation();
  useLoadPlacesLibs();

  const navigate = useNavigate();

  const auth = useAuth();
  const { handlers, onHistoryNavigation, isHistoryRedirecting } =
    useParcelHistoryNavigation(navigation, canSkipVerifyPhone);

  useEffect(() => {
    const stepNameFromUrl = location.pathname.split('/').at(-1);
    if (!stepNameFromUrl || !navigation.current?.name) return;

    // Prevent attempt to access not 1st step of the parcel creation flow
    if (navigation.current.name !== stepNameFromUrl) {
      const firstStepUrl = generateStartParcelCreationRoute();
      navigate(firstStepUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useNavigationBlocker({
    isInProgress: isParcelCreationInProgress,
    Route: AppRoute.PARCEL_CREATE,
    setIsInProgress: setIsParcelCreationInProgress,
    onHistoryNavigation,
    onLeave: () => {
      resetAllData();
    },
    onRemain: isGuestParcelCreation
      ? () =>
          loginGup({
            auth,
            redirectUri: location.pathname,
            isGuestFlow: true,
          })
      : undefined,
  });

  const content = isMobile ? (
    parcelCreationContentMobile(isNextStepButtonLoading)
  ) : (
    <DesktopParcelWrapper>
      <ParcelTitle>{`${t(toTranslationKey(navigation.current?.name))}`}</ParcelTitle>
      <WrapperForParcelContainers>
        {parcelCreationContentDesktop(isNextStepButtonLoading)}
      </WrapperForParcelContainers>
    </DesktopParcelWrapper>
  );

  return (
    <ParcelHistoryNavigationContext.Provider value={handlers}>
      {!isHistoryRedirecting && content}
    </ParcelHistoryNavigationContext.Provider>
  );
};

export default observer(ParcelCreation);
